import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  Mail, 
  Phone, 
  Shield,
  CheckCircle,
  AlertCircle,
  Edit,
  Save,
  X,
  User
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/Avatar'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { KYCVerificationFlow } from '../components/wizard/KYCVerificationFlow'
import { ProfileCard } from '../components/profile/ProfileCard'
import { dataPersistence } from '../services/dataPersistence'
import toast from 'react-hot-toast'

const profileSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  street: z.string().min(5, 'Street address is required'),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'State is required'),
  zipCode: z.string().min(5, 'ZIP code is required'),
})

type ProfileFormData = z.infer<typeof profileSchema>

export const ProfilePage: React.FC = () => {
  const { user, updateProfile, isIdentityVerified, verifyIdentity } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showKYC, setShowKYC] = useState(false)
  const [kycStep, setKycStep] = useState<'document-type' | 'document-front' | 'document-back' | 'selfie' | 'verifying' | 'complete'>('document-type')

  // Get data from Sales Hub
  const salesHubData = dataPersistence.getAll()
  const exchangerInfo = salesHubData.exchangerInfoPhaseOne
  
  // Parse names from Sales Hub
  const parseNames = () => {
    if (exchangerInfo?.names) {
      const nameParts = exchangerInfo.names.trim().split(/\s+/)
      if (nameParts.length >= 2) {
        return {
          firstName: nameParts[0],
          lastName: nameParts.slice(1).join(' ')
        }
      }
      return { firstName: nameParts[0] || '', lastName: '' }
    }
    return { firstName: '', lastName: '' }
  }
  
  const parsedNames = parseNames()
  
  // Parse address from Sales Hub
  const parseAddress = () => {
    if (exchangerInfo?.homeAddress) {
      const addressParts = exchangerInfo.homeAddress.split(',').map((p: string) => p.trim())
      if (addressParts.length >= 3) {
        const stateZip = addressParts[2].trim().split(/\s+/)
        return {
          street: addressParts[0] || '',
          city: addressParts[1] || '',
          state: stateZip[0] || '',
          zipCode: stateZip[1] || ''
        }
      }
    }
    return { street: '', city: '', state: '', zipCode: '' }
  }
  
  const parsedAddress = parseAddress()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: parsedNames.firstName || user?.firstName || '',
      lastName: parsedNames.lastName || user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      dateOfBirth: user?.dateOfBirth || '',
      street: parsedAddress.street || user?.address?.street || '',
      city: parsedAddress.city || user?.address?.city || '',
      state: parsedAddress.state || user?.address?.state || '',
      zipCode: parsedAddress.zipCode || user?.address?.zipCode || '',
    }
  })
  
  // Update form when Sales Hub data changes
  useEffect(() => {
    if (exchangerInfo?.names) {
      setValue('firstName', parsedNames.firstName)
      setValue('lastName', parsedNames.lastName)
    }
    if (exchangerInfo?.homeAddress) {
      setValue('street', parsedAddress.street)
      setValue('city', parsedAddress.city)
      setValue('state', parsedAddress.state)
      setValue('zipCode', parsedAddress.zipCode)
    }
  }, [exchangerInfo, setValue])

  const onSubmit = async (data: ProfileFormData) => {
    try {
      setIsLoading(true)
      await updateProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        dateOfBirth: data.dateOfBirth,
        address: {
          street: data.street,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode,
        }
      })
      toast.success('Profile updated successfully!')
      setIsEditing(false)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update profile')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    reset()
    setIsEditing(false)
  }

  const verificationSteps = [
    {
      id: 'identity',
      title: 'Identity Verification',
      description: 'Verify your identity with government-issued ID',
      completed: isIdentityVerified,
      required: true
    },
    {
      id: 'kyc',
      title: 'Know Your Customer (KYC)',
      description: 'Complete KYC requirements for compliance',
      completed: user?.isKycComplete || false,
      required: true
    },
    {
      id: 'accredited',
      title: 'Accredited Investor Status',
      description: 'Verify accredited investor status for certain investments',
      completed: user?.isAccreditedInvestor || false,
      required: false
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-slate-100 flex items-center gap-3">
            <User className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            Profile
          </h1>
          <p className="text-secondary-600 dark:text-slate-400 mt-1">
            Manage your account information and verification status
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSubmit(onSubmit)} disabled={isLoading}>
                {isLoading ? (
                  <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          )}
        </div>
      </div>

      {/* Profile Card */}
      {user && (
        <ProfileCard 
          user={{
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            phone: user.phone,
            isAdmin: user.isAdmin
          }} 
          currentUserId={user.id}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 dark:bg-slate-800 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-slate-100 mb-4">Personal Information</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-slate-300 mb-1">First Name</label>
                  <Input
                    {...register('firstName')}
                    disabled={!isEditing}
                    className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                  />
                  {errors.firstName && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.firstName.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-slate-300 mb-1">Last Name</label>
                  <Input
                    {...register('lastName')}
                    disabled={!isEditing}
                    className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                  />
                  {errors.lastName && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.lastName.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-slate-300 mb-1">Email Address</label>
                  <Input
                    type="email"
                    {...register('email')}
                    disabled={!isEditing}
                    className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-slate-300 mb-1">Phone Number</label>
                  <Input
                    type="tel"
                    {...register('phone')}
                    disabled={!isEditing}
                    className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                  />
                  {errors.phone && <p className="mt-1 text-red-600 dark:text-red-400">{errors.phone.message}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-slate-300 mb-1">Date of Birth</label>
                <Input
                  type="date"
                  {...register('dateOfBirth')}
                  disabled={!isEditing}
                  className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                />
                {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.dateOfBirth.message}</p>}
              </div>

              <div>
                <h3 className="text-md font-medium text-secondary-900 dark:text-slate-100 mb-3">Address</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-slate-300 mb-1">Street Address</label>
                    <Input
                      {...register('street')}
                      disabled={!isEditing}
                      className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                    />
                    {errors.street && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.street.message}</p>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-slate-300 mb-1">City</label>
                      <Input
                        {...register('city')}
                        disabled={!isEditing}
                        className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                      />
                      {errors.city && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.city.message}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-slate-300 mb-1">State</label>
                      <Input
                        {...register('state')}
                        disabled={!isEditing}
                        className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                      />
                      {errors.state && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.state.message}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-slate-300 mb-1">ZIP Code</label>
                      <Input
                        {...register('zipCode')}
                        disabled={!isEditing}
                        className="dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
                      />
                      {errors.zipCode && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.zipCode.message}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </Card>

          {/* Account Information */}
          <Card className="p-6 dark:bg-slate-800 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-slate-100 mb-4">Account Information</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-secondary-600 dark:text-slate-400">Account ID</span>
                <span className="font-mono text-sm text-secondary-900 dark:text-slate-100">{user?.id}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-secondary-600 dark:text-slate-400">Member Since</span>
                <span className="text-secondary-900 dark:text-slate-100">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-secondary-600 dark:text-slate-400">Last Updated</span>
                <span className="text-secondary-900 dark:text-slate-100">
                  {user?.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Profile Photo */}
          <Card className="p-6 text-center dark:bg-slate-800 dark:border-slate-700">
            <Avatar className="h-16 w-16 mx-auto mb-4">
              <AvatarImage src={user?.avatar} alt={`${user?.firstName} ${user?.lastName}`} />
              <AvatarFallback className="dark:bg-slate-700 dark:text-slate-100">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <h3 className="font-semibold text-secondary-900 dark:text-slate-100">
              {user?.firstName} {user?.lastName}
            </h3>
            <p className="text-sm text-secondary-600 dark:text-slate-400">{user?.email}</p>
            <Button variant="outline" size="sm" className="mt-3 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600">
              Change Photo
            </Button>
          </Card>

          {/* Verification Status */}
          <Card className="p-6 dark:bg-slate-800 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-slate-100 mb-4">Verification Status</h2>
            <div className="space-y-3">
              {verificationSteps.map((step) => (
                <div key={step.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {step.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-sm font-medium text-secondary-900 dark:text-slate-100">
                        {step.title}
                      </h3>
                      {step.required && (
                        <Badge variant="destructive" className="text-xs">
                          Required
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-secondary-600 dark:text-slate-400 mt-1">
                      {step.description}
                    </p>
                    {step.completed && (
                      <Badge variant="success" className="text-xs mt-1">
                        Completed
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {!isIdentityVerified && !showKYC && (
              <Button 
                className="w-full mt-4"
                onClick={() => setShowKYC(true)}
              >
                <Shield className="h-4 w-4 mr-2" />
                Start Verification
              </Button>
            )}
            
            {showKYC && !isIdentityVerified && (
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <KYCVerificationFlow
                  onComplete={async (data) => {
                    try {
                      await verifyIdentity(data)
                      toast.success('Identity verified successfully!')
                      setShowKYC(false)
                    } catch (error: any) {
                      toast.error(error.message || 'Verification failed')
                      throw error
                    }
                  }}
                  userName={`${user?.firstName} ${user?.lastName}`}
                  initialStep={kycStep}
                  onStepChange={setKycStep}
                />
              </div>
            )}
          </Card>

          {/* Quick Actions */}
          <Card className="p-6 dark:bg-slate-800 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-slate-100 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <Button variant="outline" size="sm" className="w-full justify-start dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600">
                <Mail className="h-4 w-4 mr-2" />
                Change Email
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600">
                <Shield className="h-4 w-4 mr-2" />
                Change Password
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600">
                <Phone className="h-4 w-4 mr-2" />
                Two-Factor Auth
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
